import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  subscribeToDemoCanvas,
  placeDemoPixel,
  getUserIdentifier,
  getUserColor,
  getCanvasSize,
  type DemoCanvasState,
} from '@/services/demoCanvas.service';
import { logger } from '@/lib/logger';

const GRID_SIZE = getCanvasSize();
const BASE_CELL_SIZE = 28; // pixels per cell (504 / 28 = 18)
const CANVAS_SIZE = 504; // fixed canvas size
const GRID_LINE_COLOR = '#80808012';

interface DemoCanvasProps {
  className?: string;
}

export const DemoCanvas = ({ className = '' }: DemoCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvas, setCanvas] = useState<DemoCanvasState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlacing, setIsPlacing] = useState(false);
  const [hoveredCell, setHoveredCell] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [cellSize, setCellSize] = useState(BASE_CELL_SIZE);
  const { user } = useAuth();
  const userColorRef = useRef<string | null>(null);
  const lastPlaceTimeRef = useRef<number>(0);
  const lastCellRef = useRef<{ x: number; y: number } | null>(null);
  const PLACE_DEBOUNCE_MS = 50; // Minimum time between pixel placements

  // Get user identifier and color
  useEffect(() => {
    const identifier = getUserIdentifier(user?.uid || null);
    userColorRef.current = getUserColor(identifier);
  }, [user]);

  // Subscribe to canvas updates
  useEffect(() => {
    setIsLoading(true);
    
    const unsubscribe = subscribeToDemoCanvas((newCanvas) => {
      setCanvas(newCanvas);
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Draw canvas
  const drawCanvas = useCallback(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl || !canvas) return;

    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);

    // Draw background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasEl.width, canvasEl.height);

    // Draw grid lines
    ctx.strokeStyle = GRID_LINE_COLOR;
    ctx.lineWidth = 1;

    for (let i = 0; i <= GRID_SIZE; i++) {
      const pos = i * cellSize;
      // Vertical lines
      ctx.beginPath();
      ctx.moveTo(pos, 0);
      ctx.lineTo(pos, canvasEl.height);
      ctx.stroke();

      // Horizontal lines
      ctx.beginPath();
      ctx.moveTo(0, pos);
      ctx.lineTo(canvasEl.width, pos);
      ctx.stroke();
    }

    // Draw pixels
    for (let y = 0; y < GRID_SIZE; y++) {
      for (let x = 0; x < GRID_SIZE; x++) {
        const pixel = canvas[y][x];
        if (pixel) {
          ctx.fillStyle = pixel.color;
          ctx.fillRect(x * cellSize + 1, y * cellSize + 1, cellSize - 2, cellSize - 2);
        }
      }
    }

    // Draw hover effect
    if (hoveredCell && userColorRef.current) {
      const { x, y } = hoveredCell;
      ctx.fillStyle = userColorRef.current;
      ctx.globalAlpha = 0.5;
      ctx.fillRect(x * cellSize + 1, y * cellSize + 1, cellSize - 2, cellSize - 2);
      ctx.globalAlpha = 1.0;
    }
  }, [canvas, hoveredCell, cellSize]);

  // Redraw when canvas or hover changes
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Handle canvas resize
  useEffect(() => {
    const canvasEl = canvasRef.current;
    const container = containerRef.current;
    if (!canvasEl || !container) return;

    const updateCanvasSize = () => {
      // Fixed canvas size: 504x504
      const size = CANVAS_SIZE;
      setCellSize(BASE_CELL_SIZE);
      
      canvasEl.width = size;
      canvasEl.height = size;
      canvasEl.style.width = `${size}px`;
      canvasEl.style.height = `${size}px`;
      drawCanvas();
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [drawCanvas]);

  // Get cell coordinates from mouse/touch position
  const getCellFromPosition = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const canvasEl = canvasRef.current;
    const container = containerRef.current;
    if (!canvasEl || !container) return null;

    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const cellX = Math.floor(x / cellSize);
    const cellY = Math.floor(y / cellSize);

    if (cellX >= 0 && cellX < GRID_SIZE && cellY >= 0 && cellY < GRID_SIZE) {
      return { x: cellX, y: cellY };
    }

    return null;
  }, [cellSize]);

  // Bresenham's line algorithm to get all points between two coordinates
  const getLinePoints = useCallback(
    (
      x0: number,
      y0: number,
      x1: number,
      y1: number
    ): Array<{ x: number; y: number }> => {
      const points: Array<{ x: number; y: number }> = [];

      const dx = Math.abs(x1 - x0);
      const dy = Math.abs(y1 - y0);
      const sx = x0 < x1 ? 1 : -1;
      const sy = y0 < y1 ? 1 : -1;
      let err = dx - dy;

      let x = x0;
      let y = y0;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        points.push({ x, y });

        if (x === x1 && y === y1) break;

        const e2 = 2 * err;
        if (e2 > -dy) {
          err -= dy;
          x += sx;
        }
        if (e2 < dx) {
          err += dx;
          y += sy;
        }
      }

      return points;
    },
    []
  );

  // Place pixel
  const handlePlacePixel = useCallback(async (x: number, y: number) => {
    if (isPlacing) return;
    
    const now = Date.now();
    if (now - lastPlaceTimeRef.current < PLACE_DEBOUNCE_MS) {
      return;
    }
    lastPlaceTimeRef.current = now;

    if (!userColorRef.current) {
      const identifier = getUserIdentifier(user?.uid || null);
      userColorRef.current = getUserColor(identifier);
    }

    setIsPlacing(true);
    try {
      const identifier = getUserIdentifier(user?.uid || null);
      await placeDemoPixel(x, y, userColorRef.current, identifier);
    } catch (error) {
      logger('demoCanvas').error('Error placing pixel:', error);
    } finally {
      setIsPlacing(false);
    }
  }, [user, isPlacing]);

  // Place pixels along a line (for smooth drawing)
  const handlePlacePixelsLine = useCallback((
    from: { x: number; y: number },
    to: { x: number; y: number }
  ) => {
    if (!userColorRef.current) {
      const identifier = getUserIdentifier(user?.uid || null);
      userColorRef.current = getUserColor(identifier);
    }

    const points = getLinePoints(from.x, from.y, to.x, to.y);
    const identifier = getUserIdentifier(user?.uid || null);

    // Place all pixels in the line (fire-and-forget for performance)
    // Don't await to avoid blocking, but handle errors silently
    points.forEach((point) => {
      placeDemoPixel(point.x, point.y, userColorRef.current!, identifier).catch((error) => {
        logger('demoCanvas').error('Error placing pixel in line:', error);
      });
    });
  }, [user, getLinePoints]);

  // Mouse handlers
  const handleMouseLeave = useCallback(() => {
    setHoveredCell(null);
    setIsDragging(false);
    lastCellRef.current = null;
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only allow left click (button 0)
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();
    setIsDragging(true);
    const cell = getCellFromPosition(e.clientX, e.clientY);
    if (cell) {
      lastCellRef.current = cell;
      handlePlacePixel(cell.x, cell.y);
    }
  }, [getCellFromPosition, handlePlacePixel]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    lastCellRef.current = null;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const cell = getCellFromPosition(e.clientX, e.clientY);
    setHoveredCell(cell);
    
    if (isDragging && cell) {
      if (lastCellRef.current && 
          (lastCellRef.current.x !== cell.x || lastCellRef.current.y !== cell.y)) {
        // Fill all cells between last position and current position
        handlePlacePixelsLine(lastCellRef.current, cell);
        lastCellRef.current = cell;
      } else if (!lastCellRef.current) {
        // First pixel in drag
        handlePlacePixel(cell.x, cell.y);
        lastCellRef.current = cell;
      }
    }
  }, [getCellFromPosition, handlePlacePixel, handlePlacePixelsLine, isDragging]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    const cell = getCellFromPosition(touch.clientX, touch.clientY);
    if (cell) {
      lastCellRef.current = cell;
      handlePlacePixel(cell.x, cell.y);
    }
  }, [getCellFromPosition, handlePlacePixel]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    const cell = getCellFromPosition(touch.clientX, touch.clientY);
    setHoveredCell(cell);
    if (cell) {
      if (lastCellRef.current && 
          (lastCellRef.current.x !== cell.x || lastCellRef.current.y !== cell.y)) {
        // Fill all cells between last position and current position
        handlePlacePixelsLine(lastCellRef.current, cell);
        lastCellRef.current = cell;
      } else if (!lastCellRef.current) {
        // First pixel in touch
        handlePlacePixel(cell.x, cell.y);
        lastCellRef.current = cell;
      }
    }
  }, [getCellFromPosition, handlePlacePixel, handlePlacePixelsLine]);

  const handleTouchEnd = useCallback(() => {
    setHoveredCell(null);
    lastCellRef.current = null;
  }, []);

  // Prevent context menu on right click
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  if (isLoading) {
    return (
      <div className={`relative w-full aspect-square max-w-[504px] rounded-2xl overflow-hidden border border-border bg-card flex items-center justify-center ${className}`}>
        <div className="text-muted-foreground text-sm">Loading canvas...</div>
      </div>
    );
  }

  // Explicitly type canvas props to avoid React warnings about invalid event handlers
  const canvasProps: React.CanvasHTMLAttributes<HTMLCanvasElement> = {
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onContextMenu: handleContextMenu,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    className: 'w-full h-full cursor-crosshair',
    style: { touchAction: 'none' },
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full aspect-square max-w-[504px] rounded-2xl overflow-hidden border border-border bg-card group ${className}`}
    >
      <canvas ref={canvasRef} {...canvasProps} />
      {/* Floating UI Elements */}
      <div className="absolute top-6 left-6 bg-card/90 backdrop-blur px-3 py-1.5 rounded-md border border-border flex items-center gap-2 pointer-events-none">
        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
        <span className="text-xs font-bold">Live Updates</span>
      </div>
      <div className="absolute bottom-6 right-6 bg-primary text-primary-foreground px-3 py-1.5 rounded-md flex items-center gap-2 pointer-events-none">
        <span className="material-symbols-outlined text-[16px]">
          brush
        </span>
        <span className="text-xs font-bold">Place Pixel</span>
      </div>
    </div>
  );
};

